// import { OpportunityDetailFieldKey } from '../../NewIdeationXPLR/hooks/useOpportunityDetailsForm';
// import { fieldAPIKeyMap } from '../../NewIdeationXPLR/NewIdeation/constants';

import { fieldAPIKeyMap } from "../../NewIdeationXPLR/shared/constants";
import { OpportunityDetailFieldKey } from "../../NewIdeationXPLR/shared/types";
import { CONVERSATION_TYPE } from "../constants";

// Solution field values (correct backend field_type values)
const SOLUTION_FIELDS = [
  'opportunity_statement_solutionxplr',
  'current_process_solutionxplr',
  'proposed_enhancements_solutionxplr',
  'estimated_impact_solutionxplr',
];

/**
 * Returns conversation_type based on field value
 */
export const getConversationTypeFromField = (field: string): string => {
  if (SOLUTION_FIELDS.includes(field)) {
    return CONVERSATION_TYPE.SOLUTION;
  }
  return CONVERSATION_TYPE.IDEATION;
};

/**
 * Returns conversation_type based on category name
 */
export const getConversationTypeFromCategory = (category: string): string | undefined => {
  if (category === 'Ideation XPLR') return CONVERSATION_TYPE.IDEATION;
  if (category === 'Solution XPLR') return CONVERSATION_TYPE.SOLUTION;

  const solutionCategories = [
    'Opportunity Statement',
    'Current Process (Solution)',
    'Proposed Enhancements',
    'Estimated Impact (Solution)',
  ];
  if (solutionCategories.includes(category)) return CONVERSATION_TYPE.SOLUTION;

  const ideationCategories = [
    'Current Process',
    'Expected Impact',
    'Specific Ideas of Improvement',
    'Key Performance Indicators',
  ];
  if (ideationCategories.includes(category)) return CONVERSATION_TYPE.IDEATION;

  return undefined; // 'All' or unknown → no filter
};

/**
 * Maps categories to readable field names
 */
export const categoryFieldMap = (category: string) => {
  // Check Solution-specific fields first (before generic matches)
  if (category?.includes('current_process_solutionxplr') || category === 'Current Process (Solution)') {
    return 'Current Process (Solution)';
  } else if (category?.includes('opportunity_statement_solutionxplr') || category?.includes('Opportunity Statement')) {
    return 'Opportunity Statement';
  } else if (category?.includes('proposed_enhancements_solutionxplr') || category?.includes('Proposed Enhancements')) {
    return 'Proposed Enhancements';
  } else if (category?.includes('estimated_impact_solutionxplr') || category === 'Estimated Impact (Solution)') {
    return 'Estimated Impact (Solution)';
  // Ideation fields
  } else if (category?.includes('indicator') || category?.includes('KPI') || category?.includes('KPIs')) {
    return 'Key Performance Indicators';
  } else if (category?.toLowerCase()?.includes('improvement') || category?.includes('idea')) {
    return 'Specific Idea(s) for Improvement';
  } else if (category?.includes('impact') || category?.includes('expected')) {
    return 'Expected Impact';
  } else if (category?.includes('process') || category?.includes('current')) {
    return 'Current Process';
  } else {
    return category;
  }
};

/**
 * Strips HTML tags from a string
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Gets the API field type from a category string
 */
export const getFieldTypeFromCategory = (category: string): string | undefined => {
  if (category === 'All' || category === 'Ideation XPLR' || category === 'Solution XPLR') {
    return undefined;
  }
  const categoryToFieldMap: Record<string, string> = {
    'Current Process': fieldAPIKeyMap.currentProcess,
    'Expected Impact': fieldAPIKeyMap.expectedImpact,
    'Key Performance Indicators': fieldAPIKeyMap.keyPerformanceIndicators,
    'Specific Idea(s) for Improvement': fieldAPIKeyMap.specificIdeasOfImprovement,
    'Specific Ideas of Improvement': fieldAPIKeyMap.specificIdeasOfImprovement,
    // Solution categories
    'Opportunity Statement': 'opportunity_statement_solutionxplr',
    'Current Process (Solution)': 'current_process_solutionxplr',
    'Proposed Enhancements': 'proposed_enhancements_solutionxplr',
    'Estimated Impact (Solution)': 'estimated_impact_solutionxplr',
  };
  return categoryToFieldMap[category];
};

/**
 * Gets the component field key from an API field type
 */
export const getFieldKeyFromAPIType = (apiType: string): OpportunityDetailFieldKey => {
  const apiToFieldKeyMap: Record<string, OpportunityDetailFieldKey> = {
    [fieldAPIKeyMap.currentProcess]: 'currentProcess',
    [fieldAPIKeyMap.expectedImpact]: 'expectedImpact',
    [fieldAPIKeyMap.specificIdeasOfImprovement]: 'specificIdeasOfImprovement',
    [fieldAPIKeyMap.keyPerformanceIndicators]: 'keyPerformanceIndicators',
    current_process: 'currentProcess',
    expected_impact: 'expectedImpact',
    ideas_of_improvement: 'specificIdeasOfImprovement',
    key_performance_indicator: 'keyPerformanceIndicators',
    // Solution fields — map to specialized fields in NewIdeationState
    opportunity_statement_solutionxplr: 'opportunityStatementSolutionXplr',
    current_process_solutionxplr: 'currentProcessSolutionXplr',
    proposed_enhancements_solutionxplr: 'proposedEnhancementsSolutionXplr',
    estimated_impact_solutionxplr: 'estimatedImpactSolutionXplr',
  };
  return apiToFieldKeyMap[apiType] || 'currentProcess';
};

/**
 * Gets the component field key from a category string
 */
export const getFieldKeyFromCategory = (category: string): OpportunityDetailFieldKey => {
  const categoryToFieldKeyMap: Record<string, OpportunityDetailFieldKey> = {
    'Current Process': 'currentProcess',
    'Expected Impact': 'expectedImpact',
    KPIs: 'keyPerformanceIndicators',
    'Key Performance Indicator': 'keyPerformanceIndicators',
    'Key Performance Indicators': 'keyPerformanceIndicators',
    'Specific Idea of Improvement': 'specificIdeasOfImprovement',
    'Specific Ideas of Improvement': 'specificIdeasOfImprovement',
    current_process: 'currentProcess',
    expected_impact: 'expectedImpact',
    ideas_of_improvement: 'specificIdeasOfImprovement',
    key_performance_indicator: 'keyPerformanceIndicators',
    // Solution categories — map to specialized fields in NewIdeationState
    'Opportunity Statement': 'opportunityStatementSolutionXplr',
    'Current Process (Solution)': 'currentProcessSolutionXplr',
    'Proposed Enhancements': 'proposedEnhancementsSolutionXplr',
    'Estimated Impact (Solution)': 'estimatedImpactSolutionXplr',
    opportunity_statement_solutionxplr: 'opportunityStatementSolutionXplr',
    current_process_solutionxplr: 'currentProcessSolutionXplr',
    proposed_enhancements_solutionxplr: 'proposedEnhancementsSolutionXplr',
    estimated_impact_solutionxplr: 'estimatedImpactSolutionXplr',
  };
  return categoryToFieldKeyMap[category] || 'currentProcess';
};

/**
 * Gets the snake_case API key from either a component field key or an existing API key
 */
export const getAPIKeyFromField = (field: string): string => {
  const mapping: Record<string, string> = {
    currentProcess: 'current_process',
    expectedImpact: 'expected_impact',
    specificIdeasOfImprovement: 'ideas_of_improvement',
    keyPerformanceIndicators: 'key_performance_indicator',
    current_process: 'current_process',
    expected_impact: 'expected_impact',
    ideas_of_improvement: 'ideas_of_improvement',
    key_performance_indicator: 'key_performance_indicator',
    // Solution fields
    opportunityStatementSolutionXplr: 'opportunity_statement_solutionxplr',
    currentProcessSolutionXplr: 'current_process_solutionxplr',
    proposedEnhancementsSolutionXplr: 'proposed_enhancements_solutionxplr',
    estimatedImpactSolutionXplr: 'estimated_impact_solutionxplr',
    opportunity_statement_solutionxplr: 'opportunity_statement_solutionxplr',
    current_process_solutionxplr: 'current_process_solutionxplr',
    proposed_enhancements_solutionxplr: 'proposed_enhancements_solutionxplr',
    estimated_impact_solutionxplr: 'estimated_impact_solutionxplr',
  };
  return mapping[field] || 'current_process';
};

/**
 * Gets the readable category name from an API field key or component field key
 */
export const getCategoryFromField = (field: string): string => {
  const fieldToCategoryMap: Record<string, string> = {
    current_process: 'Current Process',
    currentProcess: 'Current Process',
    expected_impact: 'Expected Impact',
    expectedImpact: 'Expected Impact',
    ideas_of_improvement: 'Specific Ideas of Improvement',
    specificIdeasOfImprovement: 'Specific Ideas of Improvement',
    key_performance_indicator: 'Key Performance Indicators',
    keyPerformanceIndicators: 'Key Performance Indicators',
    // Solution fields
    opportunity_statement_solutionxplr: 'Opportunity Statement',
    opportunityStatementSolutionXplr: 'Opportunity Statement',
    current_process_solutionxplr: 'Current Process (Solution)',
    currentProcessSolutionXplr: 'Current Process (Solution)',
    proposed_enhancements_solutionxplr: 'Proposed Enhancements',
    proposedEnhancementsSolutionXplr: 'Proposed Enhancements',
    estimated_impact_solutionxplr: 'Estimated Impact (Solution)',
    estimatedImpactSolutionXplr: 'Estimated Impact (Solution)',
  };
  return fieldToCategoryMap[field] || 'Current Process';
};

/**
 * Gets the draft storage key for a specific organization and field type
 */
export const getDraftStorageKey = (organizationId: string, fieldType: string, baseKey: string): string => {
  if (!organizationId) return baseKey;
  return `${baseKey}_${organizationId}_${fieldType}`;
};
